import { useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../components/Field';
import { Confirmation } from './Confirmation';
import { ApiError, submitApplication } from '../lib/api';
import type { ApplicationResponse } from '../lib/api';
import {
  EMPTY_FORM,
  STEP_FIELDS,
  toPayload,
  validateAll,
  validateStep,
} from '../lib/validation';
import type { FieldErrors, FormValues } from '../lib/validation';

const STEP_LABELS = ['Company', 'Contact', 'Prize & campaign', 'Tracking & consent'];

export function OnboardingForm() {
  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplicationResponse | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const lastStep = STEP_FIELDS.length - 1;

  function set<K extends keyof FormValues>(name: K, value: FormValues[K]) {
    setValues((prev) => ({ ...prev, [name]: value }));
    // Clear an existing error as the user corrects the field.
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  function focusHeading() {
    // Move focus to the step heading for screen-reader users on navigation.
    requestAnimationFrame(() => headingRef.current?.focus());
  }

  function goNext() {
    const stepErrors = validateStep(step, values);
    if (Object.keys(stepErrors).length) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      return;
    }
    setStep((s) => Math.min(s + 1, lastStep));
    focusHeading();
  }

  function goBack() {
    setSubmitError(null);
    setStep((s) => Math.max(s - 1, 0));
    focusHeading();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const allErrors = validateAll(values);
    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      // Jump to the first step that has an error.
      const firstBadStep = STEP_FIELDS.findIndex((fields) => fields.some((f) => allErrors[f]));
      if (firstBadStep >= 0) {
        setStep(firstBadStep);
        focusHeading();
      }
      return;
    }

    setSubmitting(true);
    try {
      const resp = await submitApplication(toPayload(values));
      setResult(resp);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="sponsor-a-prize" className="section bg-white">
      <div className="container-content">
        <div className="mx-auto max-w-2xl text-center">
          <span className="eyebrow">Sponsor a Prize</span>
          <h2 className="mt-4 font-heading text-3xl font-bold text-ink-900 sm:text-4xl">
            Tell us about your campaign
          </h2>
          <p className="mt-4 text-lg text-ink-500">
            A few details to get started. Our team reviews every application before any campaign
            goes live — winners are always decided by skill.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-3xl">
          {result ? (
            <Confirmation result={result} />
          ) : (
            <form onSubmit={handleSubmit} noValidate className="rounded-3xl border border-line bg-white p-6 shadow-card sm:p-8">
              {/* Progress */}
              <div className="mb-8">
                <p className="font-heading text-sm font-semibold text-primary-700">
                  Step {step + 1} of {STEP_FIELDS.length}
                </p>
                <ol className="mt-3 flex gap-2" aria-hidden>
                  {STEP_LABELS.map((label, i) => (
                    <li
                      key={label}
                      className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${
                        i <= step ? 'bg-aurora' : 'bg-surface-muted'
                      }`}
                    />
                  ))}
                </ol>
                <h3
                  ref={headingRef}
                  tabIndex={-1}
                  className="mt-4 font-heading text-xl font-semibold text-ink-900 focus:outline-none"
                >
                  {STEP_LABELS[step]}
                </h3>
              </div>

              {/* Honeypot — visually hidden, off-screen, not announced */}
              <div aria-hidden className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden">
                <label htmlFor="company_url">Leave this field empty</label>
                <input
                  id="company_url"
                  name="company_url"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={values.company_url}
                  onChange={(e) => set('company_url', e.target.value)}
                />
              </div>

              {/* Step 1 — Company */}
              {step === 0 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <TextField id="company_name" label="Company name" required
                      value={values.company_name} error={errors.company_name}
                      onChange={(e) => set('company_name', e.target.value)} autoComplete="organization" />
                  </div>
                  <TextField id="company_reg_no" label="Company registration number"
                    hint="Recommended" value={values.company_reg_no} error={errors.company_reg_no}
                    onChange={(e) => set('company_reg_no', e.target.value)} />
                  <TextField id="industry" label="Industry" required
                    value={values.industry} error={errors.industry}
                    onChange={(e) => set('industry', e.target.value)} placeholder="e.g. Automotive, Retail" />
                  <div className="sm:col-span-2">
                    <TextField id="website" label="Website" required type="url"
                      value={values.website} error={errors.website}
                      onChange={(e) => set('website', e.target.value)} placeholder="https://example.com" autoComplete="url" />
                  </div>
                </div>
              )}

              {/* Step 2 — Contact */}
              {step === 1 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <TextField id="contact_name" label="Contact person" required
                      value={values.contact_name} error={errors.contact_name}
                      onChange={(e) => set('contact_name', e.target.value)} autoComplete="name" />
                  </div>
                  <TextField id="email" label="Email address" required type="email"
                    value={values.email} error={errors.email}
                    onChange={(e) => set('email', e.target.value)} autoComplete="email" />
                  <TextField id="phone" label="Phone number" required type="tel"
                    value={values.phone} error={errors.phone}
                    onChange={(e) => set('phone', e.target.value)} autoComplete="tel" />
                </div>
              )}

              {/* Step 3 — Prize & campaign */}
              {step === 2 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <TextField id="prize_product" label="Prize or product to sponsor" required
                      value={values.prize_product} error={errors.prize_product}
                      onChange={(e) => set('prize_product', e.target.value)}
                      placeholder="e.g. a demo vehicle" />
                  </div>
                  <TextField id="prize_value" label="Prize value" required
                    value={values.prize_value} error={errors.prize_value}
                    onChange={(e) => set('prize_value', e.target.value)} placeholder="e.g. R750,000" />
                  <SelectField id="sponsorship_type" label="Is the prize fully sponsored, discounted or co-funded?" required
                    value={values.sponsorship_type} error={errors.sponsorship_type}
                    onChange={(e) => set('sponsorship_type', e.target.value as FormValues['sponsorship_type'])}>
                    <option value="">Select an option</option>
                    <option value="fully_sponsored">Fully sponsored</option>
                    <option value="discounted">Discounted</option>
                    <option value="co_funded">Co-funded</option>
                  </SelectField>
                  <div className="sm:col-span-2">
                    <TextAreaField id="campaign_objective" label="Campaign objective" required
                      value={values.campaign_objective} error={errors.campaign_objective}
                      onChange={(e) => set('campaign_objective', e.target.value)}
                      placeholder="e.g. Drive showroom traffic and product awareness" />
                  </div>
                  <TextField id="target_audience" label="Target audience" required
                    value={values.target_audience} error={errors.target_audience}
                    onChange={(e) => set('target_audience', e.target.value)} placeholder="e.g. Adults 25-45, Gauteng" />
                  <TextField id="preferred_dates" label="Preferred campaign dates" required
                    value={values.preferred_dates} error={errors.preferred_dates}
                    onChange={(e) => set('preferred_dates', e.target.value)} placeholder="e.g. Sept-Oct 2026" />
                </div>
              )}

              {/* Step 4 — Tracking, opt-ins & consent */}
              {step === 3 && (
                <div className="grid gap-5">
                  <SelectField id="qr_placement" label="Will QR codes be used in-store or online?" required
                    value={values.qr_placement} error={errors.qr_placement}
                    onChange={(e) => set('qr_placement', e.target.value as FormValues['qr_placement'])}>
                    <option value="">Select an option</option>
                    <option value="in_store">In-store</option>
                    <option value="online">Online</option>
                    <option value="both">Both in-store and online</option>
                    <option value="none">Not sure yet</option>
                  </SelectField>

                  <div className="rounded-2xl bg-surface-alt p-5">
                    <p className="font-heading text-sm font-semibold text-ink-900">Optional add-ons</p>
                    <div className="mt-3 space-y-3">
                      <CheckboxField id="wants_leads" checked={values.wants_leads}
                        onChange={(c) => set('wants_leads', c)}
                        label={<>I would like to receive <strong>opt-in leads</strong> from participants who consent to partner contact for this campaign.</>} />
                      <CheckboxField id="wants_ai_marketing" checked={values.wants_ai_marketing}
                        onChange={(c) => set('wants_ai_marketing', c)}
                        label={<>I am interested in <strong>AI marketing support</strong> for campaign content, copy and creative.</>} />
                    </div>
                  </div>

                  <TextAreaField id="image_urls" label="Product or prize image links"
                    hint="Recommended. Paste one or more image URLs, separated by commas or new lines."
                    value={values.image_urls} error={errors.image_urls}
                    onChange={(e) => set('image_urls', e.target.value)} />

                  <TextAreaField id="notes" label="Notes"
                    hint="Anything else we should know? (optional)"
                    value={values.notes} error={errors.notes}
                    onChange={(e) => set('notes', e.target.value)} />

                  <CheckboxField id="consent_terms" checked={values.consent_terms}
                    error={errors.consent_terms}
                    onChange={(c) => set('consent_terms', c)}
                    label={<>I have authority to submit this application and understand that all competitions are skill-based — the prize is guaranteed to be given away to a winner decided by skill, and participant details are only shared with explicit, recorded consent.</>} />
                </div>
              )}

              {submitError && (
                <p role="alert" className="mt-6 rounded-xl bg-accent-600/10 px-4 py-3 text-sm font-medium text-accent-600">
                  {submitError}
                </p>
              )}

              {/* Controls */}
              <div className="mt-8 flex items-center justify-between gap-3">
                {step > 0 ? (
                  <button type="button" onClick={goBack} className="btn-secondary" disabled={submitting}>
                    Back
                  </button>
                ) : (
                  <span />
                )}

                {step < lastStep ? (
                  <button type="button" onClick={goNext} className="btn-primary">
                    Continue
                    <Icon name="arrow-right" className="h-5 w-5" />
                  </button>
                ) : (
                  <button type="submit" className="btn-warm" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit application'}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default OnboardingForm;
